import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { useRole } from '../providers/RoleProvider';
import { hasPermission } from '../consts/permissions';

interface PrivateRouteProps {
  children: React.ReactNode;
  permission?: string[];
}

const CONTRACTOR_ALLOWED_PATHS = ['/contractor/setup', '/contractor/edit'];

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, permission = [] }) => {
  const { loggedIn, user } = useAuth();
  const { activeRole } = useRole();
  const location = useLocation();

  // Basic auth check
  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user || !activeRole) {
    return null; // Or a loading spinner
  }

  // Check global permissions based on active role and current path
  const isAllowed = hasPermission(activeRole, location.pathname);

  // Also check specific component permission props if passed (legacy support or extra granularity)
  // If permission prop is passed, we check if the activeRole is in that list.
  const hasExplicitPermission = permission.length ? permission.includes(activeRole) : true;

  if (!isAllowed || !hasExplicitPermission) {
    // If user is trying to access something they shouldn't, redirect them to a safe place
    // For USER role, maybe dashboard or profile. For others, maybe dashboard.
    // To avoid infinite loops, we should be careful.
    // If they are already on a safe page, don't redirect.

    // Simple fallback:
    return <Navigate to="/" replace />;
  }

  // Contractor specific logic: Only if active role is CONTRACTOR
  const needsContractorProfile = activeRole === 'CONTRACTOR' && !user.contractor;
  const isOnAllowedContractorPath = CONTRACTOR_ALLOWED_PATHS.some(path =>
    location.pathname.startsWith(path)
  );

  if (needsContractorProfile && !isOnAllowedContractorPath) {
    return <Navigate to="/contractor/setup" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
